import { inventoryViewToRestore } from '../../onboarding/practice/inventoryPractice';
import { usePracticeScope } from '../../onboarding/practice/practiceScope';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ClipboardList, Link2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { tertiaryText, baseTextSize, primaryText, secondaryText, semiBoldText, smallTextSize, textBlue, lightBlueBG, roundedLG, borderPrimary, greenTextColor, regularText, quaternaryText } from '../../../styling/stylingHelper';
import { CardWithHeader } from '../../ui/CardWithHeader';
import { TableComponent, TableComponentProps } from '../../ui/TableComponent';
import { currencyFormatter } from '../../../services/helperService';
import { CountReviewProduct, CountReviewBelowMinProduct, CountReviewConfigNotLinkedProduct, NeedsReviewProduct, NeedsReviewReason } from '../../../services/inventory/inventoryService';
import { useAppUserDefaultStore, loadTableItemsPerPageSetting, saveTableItemsPerPageSetting } from '../../../stores/appUserDefaultStore';
import OrderListModal from './OrderListModal';
import { supplierText } from './inventoryCountExport';
import { firstNotSetupProduct } from '../../onboarding/inventoryValues';
import { InventoryProductMobileCard, InventoryProductSearchInput } from './inventoryProductMobileCard';
import {
  buildBaseProductColumns,
  buildSupplierFilterOptions,
  buildInvCategoryFilterOptions,
  applyProductColumnFilters,
  sortProducts,
  actionColumn,
  renderProductActions,
  frozenStdUnitPrice,
  fmtQuantity,
  badgeBaseClass,
  grayBadgeClass,
} from './inventoryReviewShared';

export type AttentionTab = 'notLinked' | 'configNotLinked' | 'notSetup' | 'missingPrice' | 'belowMin' | 'needsReview';

/** i18n key of a needs-review reason (`inventory.review.reasonSpike`, …). */
const reasonKey = (kind: NeedsReviewReason['kind']): string =>
  `inventory.review.reason${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;

/** Reason pills of one needs-review row — the "why is this flagged" cell. */
function ReviewReasonBadges({ row, t }: { row: NeedsReviewProduct; t: ReturnType<typeof useTranslation>['t'] }) {
  return (
    <div className="flex items-center justify-start gap-1 flex-wrap">
      {row.reasons.map((reason, i) => (
        <span
          key={`${reason.kind}-${i}`}
          className={
            reason.kind === 'extraDigit' || reason.kind === 'swap'
              ? grayBadgeClass
              : cn(badgeBaseClass, 'bg-[#FEF0C7] text-[#B54708] dark:bg-[#B5470833] dark:text-[#FEC84B]')
          }
        >
          {t(reasonKey(reason.kind), reason.params)}
        </span>
      ))}
    </div>
  );
}

function getVisiblePageNumbers(currentPage: number, totalPages: number) {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage === 1) return [1, 2, 3];
  if (currentPage === totalPages) return [totalPages - 2, totalPages - 1, totalPages].filter((n) => n > 0);
  return [currentPage - 1, currentPage, currentPage + 1];
}

function AttentionProductMobileCard({
  row,
  t,
  actionHandlers,
}: {
  row: CountReviewProduct;
  t: ReturnType<typeof useTranslation>['t'];
  actionHandlers: {
    onRelink?: (row: CountReviewProduct) => void;
    onEditCustom?: (row: CountReviewProduct) => void;
    onSetPrice?: (row: CountReviewProduct) => void;
    onSetup?: (row: CountReviewProduct) => void;
    linkOnboardingId?: string;
    setupOnboardingId?: string;
  };
}) {
  return (
    <InventoryProductMobileCard
      row={row}
      t={t}
      categoryLabel={t('inventory.colInvCategory')}
      categoryValue={row.inventoryCategoryName ?? '—'}
      actionHandlers={actionHandlers}
    />
  );
}

function MobileInfoColumn({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 px-3 first:pl-0 last:pr-0 min-w-0 flex-1">
      <span className={cn(tertiaryText, 'text-[12px] leading-[18px] font-medium')}>{label}</span>
      <span className={cn(primaryText, smallTextSize, semiBoldText, 'truncate leading-5')}>{value}</span>
    </div>
  );
}

function AttentionBelowMinMobileCard({
  row,
  productLookup,
  t,
  onOpenOrderList,
}: {
  row: CountReviewBelowMinProduct;
  productLookup: Map<string, CountReviewProduct>;
  t: ReturnType<typeof useTranslation>['t'];
  onOpenOrderList?: () => void;
}) {
  const source = productLookup.get(row.productKey);
  const unitPrice = source ? frozenStdUnitPrice(source) : null;

  return (
    <div className="rounded-xl border border-[#EAECF0] dark:border-borderPrimary-dark bg-white dark:bg-background-dark p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <span className={cn(primaryText, smallTextSize, semiBoldText, 'leading-5 min-w-0')}>{row.productName}</span>
        <span className={cn(textBlue, smallTextSize, semiBoldText, lightBlueBG, roundedLG, 'px-2 py-1 whitespace-nowrap shrink-0')}>
          {`${fmtQuantity(row.currentCount)} ${row.countUnit}`}
        </span>
      </div>

      <div className="flex border-y border-[#EAECF0] dark:border-borderPrimary-dark py-3">
        <MobileInfoColumn label={t('inventory.colSupplier')} value={supplierText(row.supplier)} />
        <div className="w-px bg-[#EAECF0] dark:bg-borderPrimary-dark shrink-0" />
        <MobileInfoColumn label={t('inventory.colInvCategory')} value={source?.inventoryCategoryName ?? '—'} />
        <div className="w-px bg-[#EAECF0] dark:bg-borderPrimary-dark shrink-0" />
        <MobileInfoColumn
          label={t('inventory.unitPrice')}
          value={unitPrice ? `${currencyFormatter.format(unitPrice.value)} / ${unitPrice.unit}` : '—'}
        />
      </div>

      <div className="flex items-center justify-between pt-3">
        <div className="min-w-0">
          <span className={cn(tertiaryText, 'text-[12px] leading-[18px] font-medium')}>{t('inventory.value')} </span>
          <span className={cn(primaryText, smallTextSize, semiBoldText, 'leading-5')}>{currencyFormatter.format(row.value)}</span>
        </div>
        {onOpenOrderList && (
          <button
            type="button"
            onClick={onOpenOrderList}
            className={cn(
              'inline-flex items-center justify-center w-8 h-8 rounded-lg border shrink-0',
              borderPrimary,
              greenTextColor,
              'hover:bg-gray-50 dark:hover:bg-gray-800'
            )}
            aria-label={t('inventory.seeOrderList')}
          >
            <ClipboardList className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}

function AttentionNeedsReviewMobileCard({
  row,
  t,
}: {
  row: NeedsReviewProduct;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  return (
    <div className="rounded-xl border border-[#EAECF0] dark:border-borderPrimary-dark bg-white dark:bg-background-dark p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <span className={cn(primaryText, smallTextSize, semiBoldText, 'leading-5 min-w-0')}>{row.productName}</span>
        <span className={cn(textBlue, smallTextSize, semiBoldText, lightBlueBG, roundedLG, 'px-2 py-1 whitespace-nowrap shrink-0')}>
          {`${fmtQuantity(row.currentCount)} ${row.countUnit}`}
        </span>
      </div>

      <div className="flex border-y border-[#EAECF0] dark:border-borderPrimary-dark py-3">
        <MobileInfoColumn label={t('inventory.colSupplier')} value={supplierText(row.supplier)} />
        <div className="w-px bg-[#EAECF0] dark:bg-borderPrimary-dark shrink-0" />
        <MobileInfoColumn
          label={t('inventory.review.colUsual')}
          value={`${fmtQuantity(row.expectedCount)} ${row.countUnit}`}
        />
        <div className="w-px bg-[#EAECF0] dark:bg-borderPrimary-dark shrink-0" />
        <MobileInfoColumn label={t('inventory.value')} value={currencyFormatter.format(row.value)} />
      </div>

      <div className="pt-3">
        <ReviewReasonBadges row={row} t={t} />
      </div>
    </div>
  );
}

function AttentionMobilePagination({
  totalItems,
  itemsPerPage,
  usePagination,
  currentPage,
  onPageChange,
  onItemsPerPageChange,
}: {
  totalItems: number;
  itemsPerPage: number;
  usePagination: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (value: number) => void;
}) {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIdx = (currentPage - 1) * itemsPerPage;
  const showingFrom = totalItems === 0 ? 0 : startIdx + 1;
  const showingTo = totalItems === 0 ? 0 : Math.min(startIdx + itemsPerPage, totalItems);
  const visiblePages = getVisiblePageNumbers(currentPage, totalPages);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  if (totalItems === 0) return null;

  return (
    <div className="flex flex-row items-center justify-between pt-4">
      {usePagination ? (
        <div className="flex items-center gap-1 text-[14px] leading-5 text-[#858D9D] dark:text-[#858D9D]">
          <span>{t('common.showing')} {showingFrom}-</span>
          <div className="relative inline-block" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((open) => !open)}
              className="inline-flex items-center gap-0.5 hover:text-darkGreenColor dark:hover:text-darkGreenColor-dark transition-colors cursor-pointer focus:outline-none focus:text-darkGreenColor dark:focus:text-darkGreenColor-dark"
              type="button"
            >
              <span className="font-semibold">{showingTo}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {dropdownOpen && (
              <div className="absolute left-0 bottom-full mb-1 bg-white dark:bg-background-dark border border-[#EAECF0] dark:border-borderPrimary-dark rounded-lg shadow-lg py-1 z-50 min-w-[80px]">
                {[10, 15, 50, 100].map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      onItemsPerPageChange(option);
                      setDropdownOpen(false);
                    }}
                    className={cn(
                      'w-full px-4 py-2 text-left hover:bg-neutral-50 dark:hover:bg-backgroundDarker-dark transition-colors',
                      smallTextSize,
                      option === itemsPerPage ? cn(semiBoldText, 'text-darkGreenColor dark:text-darkGreenColor-dark') : regularText
                    )}
                    type="button"
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
          <span>{t('common.of')} {totalItems}</span>
        </div>
      ) : (
        <span className="text-[13px] text-[#667085] dark:text-[#858D9D]">
          {`${t('common.showing')} ${showingFrom}–${showingTo} ${t('common.of')} ${totalItems}`}
        </span>
      )}

      {(totalPages > 1 || usePagination) && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#0E8965] dark:border-[#0E8965] disabled:opacity-40 disabled:cursor-not-allowed transition"
            aria-label={t('common.previousPage')}
            type="button"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7525 13.8441L7.49751 10.5833C7.38886 10.474 7.32788 10.3261 7.32788 10.172C7.32788 10.0179 7.38886 9.87008 7.49751 9.76079L10.7525 6.49995C10.8341 6.41768 10.9383 6.36158 11.0519 6.33879C11.1655 6.316 11.2834 6.32755 11.3904 6.37199C11.4974 6.41642 11.5887 6.49171 11.6528 6.58826C11.7168 6.68481 11.7507 6.79825 11.75 6.91412V13.43C11.7507 13.5458 11.7168 13.6593 11.6528 13.7558C11.5887 13.8524 11.4974 13.9277 11.3904 13.9721C11.2834 14.0165 11.1655 14.0281 11.0519 14.0053C10.9383 13.9825 10.8341 13.9264 10.7525 13.8441Z" fill="#0E8965" />
            </svg>
          </button>
          <div className="flex items-center gap-1">
            {visiblePages.map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={cn(
                  'w-8 h-8 flex items-center justify-center rounded-lg text-[14px] leading-5 font-semibold tracking-[0.7px] transition',
                  pageNum === currentPage
                    ? 'bg-[#0E8965] text-white'
                    : 'border border-[#EAECF0] dark:border-[#FFFFFF14] text-[#858D9D] dark:text-[#CBD5E1] hover:bg-gray-50 dark:hover:bg-[#858D9D]/20'
                )}
                aria-current={pageNum === currentPage ? 'page' : undefined}
                type="button"
              >
                {pageNum}
              </button>
            ))}
          </div>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#0E8965] dark:border-[#0E8965] disabled:opacity-40 disabled:cursor-not-allowed transition"
            aria-label={t('common.nextPage')}
            type="button"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.25 13.4293V6.91231C8.25002 6.79696 8.28425 6.6842 8.34835 6.58829C8.41245 6.49239 8.50354 6.41764 8.61012 6.3735C8.71669 6.32936 8.83396 6.31781 8.9471 6.3403C9.06024 6.3628 9.16417 6.41834 9.24575 6.49989L12.5042 9.75839C12.6136 9.86779 12.675 10.0161 12.675 10.1708C12.675 10.3255 12.6136 10.4738 12.5042 10.5832L9.24575 13.8417C9.16417 13.9233 9.06024 13.9788 8.9471 14.0013C8.83396 14.0238 8.71669 14.0123 8.61012 13.9681C8.50354 13.924 8.41245 13.8492 8.34835 13.7533C8.28425 13.6574 8.25002 13.5447 8.25 13.4293Z" fill="#0E8965" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

function AttentionMobileList<T>({
  data,
  itemsPerPage: itemsPerPageProp,
  usePagination,
  renderItem,
}: {
  data: T[];
  itemsPerPage?: number;
  usePagination: boolean;
  renderItem: (row: T) => ReactNode;
}) {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const inPractice = usePracticeScope();
  const [practicePageSize, setPracticePageSize] = useState(5);
  const itemsPerPageFromStore = useAppUserDefaultStore((s) => s.tableItemsPerPage);
  const itemsPerPage = itemsPerPageProp || (inPractice ? practicePageSize : (usePagination ? itemsPerPageFromStore : 5));

  useEffect(() => {
    if (usePagination) loadTableItemsPerPageSetting();
  }, [usePagination]);

  useEffect(() => {
    setCurrentPage(1);
  }, [data.length, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * itemsPerPage;
  const pageData = data.slice(startIdx, startIdx + itemsPerPage);

  const handleItemsPerPageChange = async (value: number) => {
    try {
      if (inPractice) setPracticePageSize(value);
      else await saveTableItemsPerPageSetting(value);
    } catch (error) {
      console.error('Failed to save items per page setting:', error);
    }
  };

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center w-full py-8">
        <p className={cn(smallTextSize, regularText, quaternaryText, 'text-center w-full')}>
          {t('common.noDataInPeriod')}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex flex-col gap-4">
        {pageData.map((row, index) => (
          <div key={index}>{renderItem(row)}</div>
        ))}
      </div>
      <AttentionMobilePagination
        totalItems={data.length}
        itemsPerPage={itemsPerPage}
        usePagination={usePagination}
        currentPage={safePage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </div>
  );
}

export interface InventoryAttentionCardProps {
  headerActionsRight?: React.ReactNode;
  /** Products already scoped to whatever the caller wants (all, one section, …). */
  products: CountReviewProduct[];
  /**
   * Extra rows for the "Missing price" tab ONLY: configured products with no usable price
   * that no count covers (`CountReviewPayload.configMissingPrice`). Deliberately a separate
   * prop rather than part of `products` — they carry no frozen quantity or value, so they
   * must not reach the other tabs, the KPI counts or the below-minimum view.
   */
  extraMissingPriceProducts?: CountReviewProduct[];
  /**
   * Group-level below-minimum products (one entry per product, aggregated across the
   * sections it was counted in). When provided, the "Below minimum" tab renders these
   * with threshold + needed-order columns instead of per-row product flags. Undefined
   * (e.g. the section detail page) → the tab is hidden entirely (the minimum is a
   * cross-section total, so per-section flags would be misleading).
   */
  belowMinProducts?: CountReviewBelowMinProduct[];
  /**
   * Counted numbers that look like counting mistakes, detected server-side.
   * When provided, a "Needs review" tab lists them. Undefined (e.g. the section detail
   * page) hides the tab — the comparison is against the group's previous sessions, so
   * it only means something at group level.
   */
  needsReviewProducts?: NeedsReviewProduct[];
  /**
   * SUPER ADMIN ONLY — configured products with no order-history link that no count covers
   * (`CountReviewPayload.configNotLinked`, which the server omits for everyone else). When
   * provided, an extra internal tab lists them; undefined hides the tab entirely, so the
   * server's omission is the only gate this card needs.
   *
   * These are the rows the count-scoped "Not linked" tab structurally cannot show: a product
   * counted as 0 leaves no frozen item behind, and an uncounted section leaves no count at all.
   * One row per product, carrying every section assignment behind it.
   */
  configNotLinkedProducts?: CountReviewConfigNotLinkedProduct[];
  title: string;
  emptyText: string;
  itemsPerPage?: number;
  /** Optional anchor id on the wrapper (used by P2's "jump to attention"). */
  id?: string;
  /** Controlled active tab (optional — falls back to internal state). */
  activeTab?: AttentionTab;
  onTabChange?: (tab: AttentionTab) => void;
  /** When set, a "Link" action appears on not-linked rows to open the re-link modal. */
  onRelink?: (product: CountReviewProduct) => void;
  /**
   * When set, an "Edit" action appears on custom-ingredient rows (any tab) to open the
   * custom ingredient side modal. Gated on the row's source, not on the tab.
   */
  onEditCustom?: (product: CountReviewProduct) => void;
  /**
   * When set, a bulk "Link products" automation button appears on the Not linked tab.
   * Receives every not-linked row in this card's scope (NOT the search/filter subset) —
   * the automation reviews the whole tab in one pass.
   */
  onLinkAll?: (products: CountReviewProduct[]) => void;
  /** When set, a "Set price" action appears on missing-price rows to open the set-price modal. */
  onSetPrice?: (product: CountReviewProduct) => void;
  /** When set (with `costPriceKeys`), a "Setup" action appears on not-setup rows to open the setup modal. */
  onSetup?: (product: CountReviewProduct) => void;
  /**
   * Inventory keys referenced by any cost price analysis / subrecipe ingredient
   * (see `buildCostPriceInventoryKeys`). When provided, a "Not setup" tab lists
   * linked products missing from this set — they can never get an expected usage
   * in the leakage report. Undefined (still loading) hides the tab.
   */
  costPriceKeys?: Set<string>;
}

/**
 * The "Products needing attention" 3-tab block (Not linked · Missing prices · Below minimum),
 * shared by the Count Review page (P2, all products) and the section detail page (P3, one section).
 * Owns its own tab state unless the caller controls it.
 */
export const InventoryAttentionCard: React.FC<InventoryAttentionCardProps> = ({
  headerActionsRight,
  products,
  belowMinProducts,
  needsReviewProducts,
  configNotLinkedProducts,
  title,
  emptyText,
  // No default: undefined → store-driven pagination with the rows-per-page selector;
  // an explicit value pins the page size and hides the selector (TableComponent contract).
  itemsPerPage,
  id,
  activeTab,
  onTabChange,
  onRelink,
  onEditCustom,
  onLinkAll,
  onSetPrice,
  onSetup,
  costPriceKeys,
  extraMissingPriceProducts,
}) => {
  const { t } = useTranslation();
  const restaurantId = useAppUserDefaultStore(state => state.restaurant?.id);
  const restoredFilters = inventoryViewToRestore(restaurantId)?.attentionFilters as { searchQuery?: string; sortColumn?: string | null; sortDirection?: 'asc' | 'desc' | null; supplierFilters?: string[]; invCategoryFilters?: string[] } | undefined;
  const [internalTab, setInternalTab] = useState<AttentionTab>('notLinked');
  const tab = activeTab ?? internalTab;
  const setTab = (next: AttentionTab) => {
    if (onTabChange) onTabChange(next);
    else setInternalTab(next);
  };

  const [searchQuery, setSearchQuery] = useState(restoredFilters?.searchQuery ?? '');
  const [orderListOpen, setOrderListOpen] = useState(false);
  // Default: highest value on top (most relevant products first).
  const [sortColumn, setSortColumn] = useState<string | null>(restoredFilters?.sortColumn ?? 'value');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(restoredFilters?.sortDirection ?? 'desc');
  const [supplierFilters, setSupplierFilters] = useState<string[]>(restoredFilters?.supplierFilters ?? []);
  const [invCategoryFilters, setInvCategoryFilters] = useState<string[]>(restoredFilters?.invCategoryFilters ?? []);
  const inPractice = usePracticeScope();
  useEffect(() => {
    const capture = (event: Event) => { (event as CustomEvent).detail.attentionFilters = { searchQuery, sortColumn, sortDirection, supplierFilters, invCategoryFilters }; };
    window.addEventListener('onboarding:inventory-snapshot-view', capture);
    return () => window.removeEventListener('onboarding:inventory-snapshot-view', capture);
  }, [searchQuery, sortColumn, sortDirection, supplierFilters, invCategoryFilters]);

  const savedView = useRef<{ searchQuery: string; sortColumn: string | null; sortDirection: 'asc' | 'desc' | null; supplierFilters: string[]; invCategoryFilters: string[] } | null>(null);
  useEffect(() => {
    if (inPractice) {
      if (!savedView.current) savedView.current = { searchQuery, sortColumn, sortDirection, supplierFilters, invCategoryFilters };
      setSearchQuery(''); setSortColumn('value'); setSortDirection('desc'); setSupplierFilters([]); setInvCategoryFilters([]);
    } else if (restoredFilters || savedView.current) {
      const previous = restoredFilters ?? savedView.current!; savedView.current = null;
      setSearchQuery(previous.searchQuery ?? ''); setSortColumn(previous.sortColumn ?? 'value'); setSortDirection(previous.sortDirection ?? 'desc'); setSupplierFilters(previous.supplierFilters ?? []); setInvCategoryFilters(previous.invCategoryFilters ?? []);
    }
    // Snapshot exactly at the practice boundary, not after each user filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inPractice]);


  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      // First click sorts highest-first; the name column starts A→Z.
      setSortDirection(column === 'productName' ? 'asc' : 'desc');
    }
  };

  const notLinkedRows = useMemo(() => products.filter((p) => !p.linked), [products]);
  // Already scoped + deduped server-side; the tab exists only when the field was sent at all.
  const hasConfigNotLinked = configNotLinkedProducts !== undefined;
  const configNotLinkedRows = useMemo(() => configNotLinkedProducts ?? [], [configNotLinkedProducts]);
  // Linked to real order history, but absent from every cost price analysis →
  // leakage would always expect 0 usage for it.
  const notSetupRows = useMemo(
    () => (costPriceKeys ? products.filter((p) => p.linked && !costPriceKeys.has(p.productKey)) : []),
    [products, costPriceKeys]
  );
  // Counted rows that froze a 0/absent price, plus configured products that were never
  // counted at all. The server already excludes any key present in `products`, so a product
  // that is both counted and configured appears exactly once.
  const missingPriceRows = useMemo(
    () => [...products.filter((p) => p.missingPrice), ...(extraMissingPriceProducts ?? [])],
    [products, extraMissingPriceProducts]
  );

  // Group-level below-minimum view: one row per below-min product (cross-section total),
  // rendered as its own table with threshold + needed-order columns. Only shown when the
  // caller passes `belowMinProducts` (the count review page). The section detail page
  // omits it, so the tab is hidden there entirely — the minimum is a group concept and
  // per-section flags would be misleading.
  const groupBelowMin = belowMinProducts !== undefined;
  const belowMinData = useMemo(() => {
    if (!belowMinProducts) return [];
    const q = searchQuery.trim().toLowerCase();
    return q
      ? belowMinProducts.filter(
        (p) => p.productName.toLowerCase().includes(q) || p.sections.some((s) => s.toLowerCase().includes(q))
      )
      : belowMinProducts;
  }, [belowMinProducts, searchQuery]);

  const belowMinColumns: TableComponentProps<CountReviewBelowMinProduct>['columns'] = useMemo(
    () => [
      {
        key: 'productName',
        label: t('inventory.product'),
        render: (_v, row) => (
          <div className="min-w-0">
            <span className={cn(primaryText, smallTextSize, semiBoldText)}>{row.productName}</span>
            {row.sections.length > 0 && (
              <span className={cn(tertiaryText, 'block text-[14px] leading-5 font-medium')}>{row.sections.join(', ')}</span>
            )}
          </div>
        ),
      },
      {
        key: 'currentCount',
        label: t('inventory.count'),
        render: (_v, row) => (
          <span className={cn(secondaryText, smallTextSize, 'leading-5')}>{`${fmtQuantity(row.currentCount)} ${row.countUnit}`}</span>
        ),
      },
      {
        key: 'minimum',
        label: t('inventory.colThreshold'),
        render: (_v, row) => (
          <span className={cn(secondaryText, smallTextSize, 'leading-5')}>
            {row.minimum != null ? `${fmtQuantity(row.minimum)} ${row.countUnit}` : '—'}
          </span>
        ),
      },
      {
        key: 'value',
        label: t('inventory.value'),
        render: (_v, row) => (
          <span className={cn(primaryText, smallTextSize, semiBoldText, 'leading-5')}>{currencyFormatter.format(row.value)}</span>
        ),
      },
      {
        key: 'toOrder',
        label: t('inventory.colNeededOrder'),
        render: (_v, row) =>
          row.toOrder != null ? (
            <span className={cn(textBlue, smallTextSize, semiBoldText, lightBlueBG, roundedLG, 'px-2 py-1 whitespace-nowrap')}>
              {`${fmtQuantity(row.toOrder)} ${row.countUnit}`}
            </span>
          ) : (
            <span className={cn(secondaryText, smallTextSize)}>—</span>
          ),
      },
      {
        key: 'toOrderUnits',
        label: t('inventory.colOrderUnits'),
        render: (_v, row) =>
          row.toOrderUnits != null ? (
            <span className={cn(smallTextSize, semiBoldText, 'text-red-600 dark:text-red-400')}>
              {fmtQuantity(row.toOrderUnits)}
            </span>
          ) : (
            <span className={cn(secondaryText, smallTextSize)}>—</span>
          ),
      },
    ],
    [t]
  );

  // Needs-review view: its own table (the rows are cross-section aggregates with their own
  // columns, like the below-minimum tab). Hidden entirely when the caller passes nothing.
  const hasNeedsReview = needsReviewProducts !== undefined;
  const needsReviewData = useMemo(() => {
    if (!needsReviewProducts) return [];
    const q = searchQuery.trim().toLowerCase();
    return q
      ? needsReviewProducts.filter(
        (p) => p.productName.toLowerCase().includes(q) || p.sections.some((s) => s.toLowerCase().includes(q))
      )
      : needsReviewProducts;
  }, [needsReviewProducts, searchQuery]);

  const needsReviewColumns: TableComponentProps<NeedsReviewProduct>['columns'] = useMemo(
    () => [
      {
        key: 'productName',
        label: t('inventory.product'),
        render: (_v, row) => (
          <div className="min-w-0">
            <span className={cn(primaryText, smallTextSize, semiBoldText)}>{row.productName}</span>
            {row.sections.length > 0 && (
              <span className={cn(tertiaryText, 'block text-[14px] leading-5 font-medium')}>{row.sections.join(', ')}</span>
            )}
          </div>
        ),
      },
      {
        key: 'currentCount',
        label: t('inventory.review.colCounted'),
        render: (_v, row) => (
          <span className={cn(textBlue, smallTextSize, semiBoldText, lightBlueBG, roundedLG, 'px-2 py-1 whitespace-nowrap')}>
            {`${fmtQuantity(row.currentCount)} ${row.countUnit}`}
          </span>
        ),
      },
      {
        key: 'expectedCount',
        label: t('inventory.review.colUsual'),
        render: (_v, row) => (
          <span className={cn(secondaryText, smallTextSize, 'leading-5 whitespace-nowrap')}>
            {`${fmtQuantity(row.expectedCount)} ${row.countUnit}`}
            <span className={cn(tertiaryText, 'block text-[12px] leading-[18px]')}>
              {t('inventory.review.basedOnCounts', { count: row.sessionsCompared })}
            </span>
          </span>
        ),
      },
      {
        key: 'reasons',
        label: t('inventory.review.colReason'),
        render: (_v, row) => <ReviewReasonBadges row={row} t={t} />,
      },
      {
        key: 'impact',
        label: t('inventory.review.colImpact'),
        render: (_v, row) => (
          <span className={cn(primaryText, smallTextSize, semiBoldText, 'whitespace-nowrap')}>
            {currencyFormatter.format(row.impact)}
          </span>
        ),
      },
    ],
    [t]
  );

  // Filter options span the full scope so the dropdowns stay stable across tabs — the config
  // rows included, or their suppliers would be missing from the dropdown on their own tab.
  const filterOptionSource = useMemo(
    () => (configNotLinkedRows.length > 0 ? [...products, ...configNotLinkedRows] : products),
    [products, configNotLinkedRows]
  );
  const supplierOptions = useMemo(() => buildSupplierFilterOptions(filterOptionSource), [filterOptionSource]);
  const invCategoryOptions = useMemo(() => buildInvCategoryFilterOptions(t, filterOptionSource), [t, filterOptionSource]);

  const columns = useMemo(() => {
    const base = buildBaseProductColumns(t, {
      sortable: true,
      supplierFilter: {
        selectedValues: supplierFilters,
        onValuesChange: setSupplierFilters,
        availableOptions: supplierOptions,
        valueType: 'default',
        filterLabel: t('inventory.colSupplier'),
      },
      invCategoryFilter: {
        selectedValues: invCategoryFilters,
        onValuesChange: setInvCategoryFilters,
        availableOptions: invCategoryOptions,
        valueType: 'default',
        filterLabel: t('inventory.colInvCategory'),
      },
      productNameSort: {
        active: sortColumn === 'productName' ? sortDirection : null,
        onToggle: () => handleSort('productName'),
      },
    });
    return onRelink || onEditCustom || onSetPrice || onSetup
      ? [
        ...base,
        actionColumn(t, (row) =>
          renderProductActions(t, row, {
            onRelink,
            onEditCustom,
            onSetPrice,
            // "Setup" only on rows that are actually not setup (linked but in no cost price analysis).
            onSetup:
              onSetup && costPriceKeys && row.linked && !costPriceKeys.has(row.productKey) ? onSetup : undefined,
            linkOnboardingId:
              !row.linked && row.productKey === notLinkedRows[0]?.productKey
                ? 'inventory-attention-link'
                : undefined,
            setupOnboardingId:
              row.productKey === firstNotSetupProduct()?.productKey ? 'inventory-attention-setup' : undefined,
          })
        ),
      ]
      : base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, onRelink, onEditCustom, onSetPrice, onSetup, costPriceKeys, supplierFilters, invCategoryFilters, supplierOptions, invCategoryOptions, sortColumn, sortDirection]);

  const tabDefs: { key: AttentionTab; label: string; count: number; admin?: boolean }[] = [
    { key: 'notLinked', label: t('inventory.attNotLinked'), count: notLinkedRows.length },
    ...(hasConfigNotLinked
      ? [{ key: 'configNotLinked' as AttentionTab, label: t('inventory.attConfigNotLinked'), count: configNotLinkedRows.length, admin: true }]
      : []),
    ...(costPriceKeys
      ? [{ key: 'notSetup' as AttentionTab, label: t('inventory.attNotSetup'), count: notSetupRows.length }]
      : []),
    { key: 'missingPrice', label: t('inventory.attMissingPrice'), count: missingPriceRows.length },
    ...(groupBelowMin
      ? [{ key: 'belowMin' as AttentionTab, label: t('inventory.attBelowMin'), count: belowMinProducts!.length }]
      : []),
    ...(hasNeedsReview
      ? [{ key: 'needsReview' as AttentionTab, label: t('inventory.attNeedsReview'), count: needsReviewProducts!.length }]
      : []),
  ];
  // Column filters → search → sort.
  const rows = useMemo(() => {
    const tabRows =
      tab === 'notLinked' ? notLinkedRows
        : tab === 'configNotLinked' ? configNotLinkedRows
          : tab === 'notSetup' ? notSetupRows
            : tab === 'missingPrice' ? missingPriceRows
              : []; // belowMin / needsReview use their dedicated tables below

    const filtered = applyProductColumnFilters(t, tabRows, {
      suppliers: supplierFilters,
      invCategories: invCategoryFilters,
    });
    const q = searchQuery.trim().toLowerCase();
    const searched = q
      ? filtered.filter(
        (p) =>
          p.productName.toLowerCase().includes(q) ||
          (p.supplier.name ?? '').toLowerCase().includes(q) ||
          (p.supplier.platform ?? '').toLowerCase().includes(q)
      )
      : filtered;
    return sortProducts(searched, sortColumn, sortDirection);
  }, [t, tab, notLinkedRows, configNotLinkedRows, notSetupRows, missingPriceRows, supplierFilters, invCategoryFilters, searchQuery, sortColumn, sortDirection]);

  const productLookup = useMemo(() => {
    const map = new Map<string, CountReviewProduct>();
    for (const p of products) map.set(p.productKey, p);
    return map;
  }, [products]);

  const getMobileSetupHandler = (row: CountReviewProduct) =>
    onSetup && costPriceKeys && row.linked && !costPriceKeys.has(row.productKey) ? onSetup : undefined;

  const tabsBar = (
    <>
      <div className="hide-scrollbar inline-flex rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] overflow-x-auto bg-white dark:bg-background-dark w-full md:w-auto flex-shrink-0">
        {tabDefs.map((def, i) => {
          const active = tab === def.key;
          return (
            <button
              key={def.key}
              type="button"
              onClick={() => setTab(def.key)}
              data-onboarding={`inventory-att-${def.key}`}
              className={cn(
                'px-4 h-11 text-nowrap text-[14px] leading-5 font-semibold flex items-center gap-2',
                i > 0 && 'border-l border-[#E5E7EB] dark:border-[#2D4F68]',
                active
                  ? 'bg-[#EDFCF5] dark:bg-[#28A878] text-[#0E8965] dark:text-[#E8F7F2]'
                  : 'bg-white dark:bg-[#0E2B42] text-[#344054] dark:text-[#CBD5E1]'
              )}
            >
              {def.label}
              {/* Internal-only tab: the server sends its data to super admins alone, so the
                  marker tells whoever is looking that a client never sees this. */}
              {def.admin && (
                <span className="px-1.5 rounded text-[10px] leading-[18px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  {t('inventory.attAdminOnly')}
                </span>
              )}
              <span className={cn('px-1.5 min-w-5 rounded-full text-[12px] leading-[20px] font-bold text-white  ', active ? 'bg-[#0E8965] dark:bg-white dark:text-[#28A878]' : cn('bg-[#9198A4] dark:bg-[#3A6787]'))}>
                {def.count}
              </span>
            </button>
          );
        })}
      </div>
      {(tab === 'notLinked' || tab === 'configNotLinked') && onLinkAll
        && (tab === 'configNotLinked' ? configNotLinkedRows.length : notLinkedRows.length) > 0 && (
        <button
          type="button"
          onClick={() => onLinkAll(tab === 'configNotLinked' ? configNotLinkedRows : notLinkedRows)}
          className={cn('flex items-center justify-center gap-2 px-3 h-11 rounded-xl border w-full md:w-auto flex-shrink-0', smallTextSize, semiBoldText, borderPrimary, greenTextColor, 'dark:border-[#28A878] dark:text-[#28A878] dark:bg-transparent')}
        >
          <Link2 className="w-4 h-4" />
          {t('inventory.link.button')}
        </button>
      )}
      {tab === 'belowMin' && groupBelowMin && (
        <button
          type="button"
          onClick={() => setOrderListOpen(true)}
          className={cn('hidden md:flex items-center justify-center gap-2 px-3 h-11 rounded-xl border w-full md:w-auto flex-shrink-0', smallTextSize, semiBoldText, borderPrimary, greenTextColor, 'dark:border-[#28A878] dark:text-[#28A878] dark:bg-transparent')}
        >
          <ClipboardList className="w-4 h-4" />
          {t('inventory.seeOrderList')}
        </button>
      )}
    </>
  );

  const searchControl = (
    <InventoryProductSearchInput
      value={searchQuery}
      onChange={setSearchQuery}
      placeholder={t('inventory.searchProducts')}
    />
  );

  // Desktop: search sits top-right in the card header; the sub-header row below the
  // title has the tabs on the left and the order-list button (when present) on the right.
  const desktopSubHeader = (
    <div className="flex items-center justify-between gap-4 flex-wrap">{tabsBar}</div>
  );

  // Mobile keeps the stacked layout: tabs bar, then search, then the list.
  const mobileControls = (
    <div className="flex flex-col w-full gap-4">
      {tabsBar}
      {searchControl}
    </div>
  );

  return (
    <div id={id} className="scroll-mt-4">
      {products.length === 0 && configNotLinkedRows.length === 0 ? (
        <CardWithHeader title={title} headerActionsRight={headerActionsRight}>
          <p className={cn(baseTextSize, tertiaryText, 'py-8 text-center')}>{emptyText}</p>
        </CardWithHeader>
      ) : (
        <>
          <div className="hidden md:block">
            {tab === 'needsReview' && hasNeedsReview ? (
              <TableComponent<NeedsReviewProduct>
                title={title}
                headerActionsRight={headerActionsRight}
                headerActions={searchControl}
                subHeader={desktopSubHeader}
                data={needsReviewData}
                columns={needsReviewColumns}
                itemsPerPage={itemsPerPage}
                usePagination={itemsPerPage === undefined}
                blankLines={false}
                options={{ borderless: true, leftAlignCells: true, noBlankPadding: true }}
              />
            ) : tab === 'belowMin' && groupBelowMin ? (
              <TableComponent<CountReviewBelowMinProduct>
                title={title}
                headerActionsRight={headerActionsRight}
                headerActions={searchControl}
                subHeader={desktopSubHeader}
                data={belowMinData}
                columns={belowMinColumns}
                itemsPerPage={itemsPerPage}
                usePagination={itemsPerPage === undefined}
                blankLines={false}
                options={{ borderless: true, leftAlignCells: true, noBlankPadding: true }}
              />
            ) : (
              <TableComponent
                title={title}
                headerActionsRight={headerActionsRight}
                headerActions={searchControl}
                subHeader={desktopSubHeader}
                data={rows}
                columns={columns}
                itemsPerPage={itemsPerPage}
                usePagination={itemsPerPage === undefined}
                blankLines={false}
                options={{ borderless: true, leftAlignCells: true, noBlankPadding: true }}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            )}
          </div>

          <div className="md:hidden flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className={cn(semiBoldText, primaryText, 'text-[18px] leading-[24px]')}>{title}</h2>
              {headerActionsRight}
            </div>
            <div className="w-full flex flex-col gap-4">
              {mobileControls}
              {tab === 'needsReview' && hasNeedsReview ? (
                <AttentionMobileList
                  data={needsReviewData}
                  itemsPerPage={itemsPerPage}
                  usePagination={itemsPerPage === undefined}
                  renderItem={(row) => <AttentionNeedsReviewMobileCard key={row.productKey} row={row} t={t} />}
                />
              ) : tab === 'belowMin' && groupBelowMin ? (
                <AttentionMobileList
                  data={belowMinData}
                  itemsPerPage={itemsPerPage}
                  usePagination={itemsPerPage === undefined}
                  renderItem={(row) => (
                    <AttentionBelowMinMobileCard
                      key={row.productKey}
                      row={row}
                      productLookup={productLookup}
                      t={t}
                      onOpenOrderList={() => setOrderListOpen(true)}
                    />
                  )}
                />
              ) : (
                <AttentionMobileList
                  data={rows}
                  itemsPerPage={itemsPerPage}
                  usePagination={itemsPerPage === undefined}
                  renderItem={(row) => (
                    <AttentionProductMobileCard
                      key={row.productKey}
                      row={row}
                      t={t}
                      actionHandlers={{
                        onRelink,
                        onEditCustom,
                        onSetPrice,
                        onSetup: getMobileSetupHandler(row),
                        linkOnboardingId:
                          !row.linked && row.productKey === notLinkedRows[0]?.productKey
                            ? 'inventory-attention-link'
                            : undefined,
                        setupOnboardingId:
                          row.productKey === firstNotSetupProduct()?.productKey ? 'inventory-attention-setup' : undefined,
                      }}
                    />
                  )}
                />
              )}
            </div>
          </div>
        </>
      )}
      {groupBelowMin && (
        <OrderListModal isOpen={orderListOpen} onClose={() => setOrderListOpen(false)} products={belowMinProducts!} />
      )}
    </div>
  );
};

export default InventoryAttentionCard;
